import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

/**
 * VendorContextService
 * --------------------
 * Resolves the "effective vendor UID" for all business dashboard queries.
 *
 * - If the logged-in user is a VENDOR  → returns their own uid.
 * - If the logged-in user is a WORKER  → returns their parentUid (the vendor they belong to).
 *
 * All business view components MUST use getEffectiveUid() instead of
 * auth.currentUser.uid so that workers see the vendor's data.
 */
@Injectable({ providedIn: 'root' })
export class VendorContextService {
  private effectiveUid$ = new BehaviorSubject<string | null>(null);

  constructor(private auth: Auth, private firestore: Firestore) {
    onAuthStateChanged(this.auth, async (user) => {
      if (!user) {
        this.effectiveUid$.next(null);
        return;
      }
      try {
        const userSnap = await getDoc(doc(this.firestore, 'users-list', user.uid));
        if (!userSnap.exists()) {
          this.effectiveUid$.next(user.uid);
          return;
        }
        const data = userSnap.data() as any;
        // Worker: use the vendor's UID for all queries
        if (data.role === 'worker' && data.parentUid) {
          this.effectiveUid$.next(data.parentUid);
        } else {
          this.effectiveUid$.next(user.uid);
        }
      } catch (e) {
        console.error('VendorContextService: error resolving UID', e);
        this.effectiveUid$.next(user.uid);
      }
    });
  }

  /**
   * Returns a Promise that resolves to the effective vendor UID.
   * Waits until auth state + Firestore lookup are complete.
   * Use this in ngOnInit / async methods of business components.
   */
  getEffectiveUid(): Promise<string> {
    return this.effectiveUid$.pipe(
      filter((uid): uid is string => uid !== null),
      first(),
    ).toPromise() as Promise<string>;
  }

  /** Synchronous snapshot — may be null if not yet resolved. */
  get snapshot(): string | null {
    return this.effectiveUid$.value;
  }
}
